import { Activity } from '../types';
import api from '../utils/api';

export interface ActivityResponse {
  activities: Activity[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ActivityStats {
  total: number;
  totalActivities?: number;
  byType: Array<{
    type: string;
    count: number;
  }>;
  byDay: Array<{
    date: string;
    count: number;
  }>;
}

export type ActivityType =
  | 'file_upload'
  | 'file_download'
  | 'file_delete'
  | 'file_share'
  | 'file_move'
  | 'file_rename'
  | 'folder_create'
  | 'folder_update'
  | 'folder_delete'
  | 'share_create'
  | 'share_delete'
  | 'user_login'
  | 'user_logout'
  | 'user_settings_change';

const toActivityResponse = (payload: any, page: number, limit: number): ActivityResponse => {
  if (!payload) {
    return { activities: [], total: 0, page, pageSize: limit };
  }

  // Already normalized
  if (Array.isArray(payload.activities)) {
    return {
      activities: payload.activities,
      total: payload.total ?? payload.activities.length,
      page: payload.page ?? page,
      pageSize: payload.pageSize ?? payload.limit ?? limit,
    };
  }

  // { success, data, pagination }
  if (Array.isArray(payload.data)) {
    return {
      activities: payload.data,
      total: payload.pagination?.total ?? payload.data.length,
      page: payload.pagination?.page ?? page,
      pageSize: payload.pagination?.limit ?? limit,
    };
  }

  // Nested data wrapper
  if (payload.data && Array.isArray(payload.data.activities)) {
    return toActivityResponse(payload.data, page, limit);
  }

  if (Array.isArray(payload.logs)) {
    return {
      activities: payload.logs,
      total: payload.total ?? payload.logs.length,
      page: payload.page ?? page,
      pageSize: payload.limit ?? limit,
    };
  }

  if (Array.isArray(payload)) {
    return { activities: payload, total: payload.length, page, pageSize: limit };
  }

  return { activities: [], total: 0, page, pageSize: limit };
};

const activityService = {
  async getActivities(
    page: number = 1,
    limit: number = 20,
    type?: ActivityType | string,
    startDate?: string,
    endDate?: string
  ): Promise<ActivityResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/activities?${params.toString()}`);
    return toActivityResponse(response.data, page, limit);
  },

  async getFileActivities(fileId: string, page: number = 1, pageSize: number = 20): Promise<ActivityResponse> {
    return activityService.getResourceActivities('file', fileId, page, pageSize);
  },

  async getFolderActivities(folderId: string, page: number = 1, pageSize: number = 20): Promise<ActivityResponse> {
    return activityService.getResourceActivities('folder', folderId, page, pageSize);
  },

  getUserActivities: async (
    userId: string,
    page: number = 1,
    limit: number = 20,
    type?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ActivityResponse> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('userId', userId);
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    try {
      // Tenant feed supports filtering by user (admin); fall back to current-user feed
      const response = await api.get(`/activities/tenant?${params.toString()}`);
      return toActivityResponse(response.data, page, limit);
    } catch {
      const response = await api.get(`/activities?${params.toString()}`);
      return toActivityResponse(response.data, page, limit);
    }
  },

  getResourceActivities: async (
    resourceType: 'file' | 'folder',
    resourceId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ActivityResponse> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('resourceType', resourceType);
    if (resourceType === 'file') {
      params.append('fileId', resourceId);
    } else {
      params.append('folderId', resourceId);
    }

    const response = await api.get(`/activities?${params.toString()}`);
    return toActivityResponse(response.data, page, limit);
  },

  exportActivities: async (
    format: 'csv' | 'json',
    type?: string,
    startDate?: string,
    endDate?: string
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('format', format);
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/activities/export?${params.toString()}`, {
      responseType: 'blob',
    });

    return response.data;
  },

  getActivityStats: async (
    userId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ActivityStats> => {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    // Backend primarily keys off period; derive a coarse period from the range
    if (startDate && endDate) {
      const ms = Math.max(0, new Date(endDate).getTime() - new Date(startDate).getTime());
      const days = Math.ceil(ms / (24 * 60 * 60 * 1000)) || 7;
      if (days <= 1) params.append('period', '1d');
      else if (days <= 7) params.append('period', '7d');
      else if (days <= 30) params.append('period', '30d');
      else params.append('period', '90d');
    } else {
      params.append('period', '30d');
    }

    const response = await api.get(`/activities/stats?${params.toString()}`);
    const data = response.data?.data ?? response.data ?? {};
    return {
      total: Number(data.total ?? data.totalActivities ?? 0) || 0,
      totalActivities: data.totalActivities,
      byType: Array.isArray(data.byType)
        ? data.byType
        : data.byType && typeof data.byType === 'object'
          ? Object.entries(data.byType).map(([type, count]) => ({ type, count: Number(count) || 0 }))
          : [],
      byDay: Array.isArray(data.byDay)
        ? data.byDay
        : data.byDay && typeof data.byDay === 'object'
          ? Object.entries(data.byDay).map(([date, count]) => ({ date, count: Number(count) || 0 }))
          : [],
    };
  },
};

export default activityService;
